import { FieldHint } from "@/components/field-hint/FieldHint.tsx";
import { Datepicker } from "@/components/datepicker/Datepicker.tsx";
import { JobProfileSelect } from "@/components/select/JobProfileSelect.tsx";
import {
  SelectItem,
  useModals,
  Modal,
  ModalProps,
  Button,
  Select,
  Stack,
  Textarea,
  Title,
} from "@skailer-com/uikit";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import {
  useEntityReferenceOptions,
  useEntityReferenceValues,
} from "@/hooks/use-entity-reference-options.hook.ts";
import { useSystemTableConfig } from "@/hooks/use-system-table-config.hook.ts";
import {
  ChangesPreview,
  ChangesPreviewItem,
} from "@/pages/employees/employee/components/ChangesPreview.tsx";
import { useEmployeePositionSave } from "@/queries/employees/use-employee-positions.query.ts";
import useEmployeeSearchInline from "@/queries/employees/use-employees.query.ts";
import { useDepartmentsTree } from "@/queries/use-departments.query.ts";
import { useDivisionsSearchInline } from "@/queries/use-divisitions.query.ts";
import { useLocations } from "@/queries/use-locations.query.ts";
import { useOrganizationListByType } from "@/queries/use-organization-lists.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiEmployeeAssignPositionPayload,
  ApiEmployeePosition,
} from "@/types/employees/employee-position.types.ts";
import { ApiEmployee } from "@/types/employees/employee.types.ts";
import { getJobProfileTitle } from "@/types/job-profile.types.ts";
import { ApiOrganizationListType } from "@/types/organization-list.types.ts";
import { EmployeeFieldPermission } from "@/types/role.types.ts";
import { ApiFieldTypes, ApiTableTypes } from "@/types/custom-field.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { OrganizationListEditModal } from "@/components/organization-list/OrganizationListEditModal.tsx";

export interface EmployeePositionEditModalProps extends ModalProps {
  employee: ApiEmployee;
  employeePosition?: ApiEmployeePosition;
}

export function EmployeePositionEditModal({
  employee,
  employeePosition,
  ...props
}: EmployeePositionEditModalProps) {
  const { t } = useTranslation();
  const { renderEmployeeName, renderDate } = useFormatter();
  const { canEditField } = usePermissions();
  const { id } = { ...props };
  const { openModal, close } = useModals();
  const { fields, customFields, isMandatory } = useSystemTableConfig(
    ApiTableTypes.employee_positions
  );
  const {
    getFieldByType,
    getValueForFieldFromValueObject,
    getCustomFieldPreviewValue,
    getZodSchemaForField,
    transformFormDataToValue,
  } = useCustomFieldsHook();

  const optionalNumber = (type: ApiFieldTypes) =>
    isMandatory(type)
      ? z.number({ error: t("errors:field_required") })
      : z.number().nullable().optional();

  const EmployeePositionSchemaObject = z.object({
    job_profile_id: optionalNumber(ApiFieldTypes.employee_position_job_profile),
    date_effective_from: z.date({ error: t("errors:field_required") }),
    reporting_to_id: optionalNumber(ApiFieldTypes.employee_position_reporting_to),
    department_id: optionalNumber(ApiFieldTypes.employee_position_department),
    division_id: optionalNumber(ApiFieldTypes.employee_position_division),
    location_id: optionalNumber(ApiFieldTypes.employee_position_location),
    comment: isMandatory(ApiFieldTypes.employee_position_comment)
      ? z.string({ error: t("errors:field_required") }).min(1, t("errors:field_required"))
      : z.string().nullable().optional(),
    change_reason_id: optionalNumber(ApiFieldTypes.employee_position_change_reason),
    ...Object.assign({}, ...customFields.map((field) => getZodSchemaForField(field))),
  });

  const currentPosition = useMemo(() => {
    const employeeCurrentPosition = employee.employee_position;
    const division = employeePosition?.division ?? employeeCurrentPosition?.division;
    const department = employeePosition?.department ?? employeeCurrentPosition?.department;
    const jobProfile = employeePosition?.job_profile ?? employeeCurrentPosition?.job_profile;
    const location = employeePosition?.location ?? employeeCurrentPosition?.location;
    const manager = employeePosition?.manager ?? employeeCurrentPosition?.manager;
    const changeReason =
      employeePosition?.change_reason ?? employeeCurrentPosition?.change_reason ?? null;

    return {
      id: employeePosition?.id ?? employeeCurrentPosition?.id ?? 0,
      date_effective_from: employeePosition?.date_effective_from,
      job_profile: jobProfile?.is_archived ? undefined : jobProfile,
      department: department?.is_archived ? undefined : department,
      division: division?.is_archived ? undefined : division,
      manager: manager?.is_archived ? undefined : manager,
      location: location?.is_archived ? undefined : location,
      comment: employeePosition?.comment,
      change_reason: changeReason?.is_archived ? null : changeReason,
      custom_fields: employeePosition?.custom_fields ?? [],
    };
  }, [employeePosition, employee]);

  const [changeItems, setChangeItems] = useState<ChangesPreviewItem[]>(() => {
    return [
      {
        id: "date_effective_from",
        title: t("common:date_effective_from"),
        currentValue: employeePosition?.id ? renderDate(employeePosition.date_effective_from) : "",
      },
      {
        id: "change_reason_id",
        title: t("core:employee_position_change_reason"),
        currentValue: currentPosition.change_reason?.title ?? "",
      },
      {
        id: "job_profile_id",
        title: t("core:job_profile"),
        currentValue: currentPosition.job_profile
          ? getJobProfileTitle(currentPosition.job_profile)
          : "",
      },
      {
        id: "department_id",
        title: t("core:department"),
        currentValue: currentPosition.department?.title ?? "",
      },
      {
        id: "division_id",
        title: t("core:division"),
        currentValue: currentPosition.division?.title ?? "",
      },
      {
        id: "reporting_to_id",
        title: t("core:manager"),
        currentValue: currentPosition.manager ? renderEmployeeName(currentPosition.manager) : "",
      },

      {
        id: "location_id",
        title: t("common:location"),
        currentValue: currentPosition.location?.title ?? "",
      },
      {
        id: "comment",
        title: t("common:comment"),
        currentValue: currentPosition.comment ?? "",
      },
      ...customFields.map((field) => {
        const customField = currentPosition.custom_fields.find(
          (item) => item.field.id === field.id
        );
        const currentFieldData = getValueForFieldFromValueObject(field, customField?.value, true);
        return {
          id: field.slug,
          title: field.title,
          currentValue: getCustomFieldPreviewValue(
            field,
            currentFieldData,
            customField?.value?.value_references ?? []
          ),
        };
      }),
    ];
  });

  const [isPreviewChanges, setIsPreviewChanges] = useState(false);

  const departments = useDepartmentsTree();
  const divisions = useDivisionsSearchInline();
  const managerSearch = useEmployeeSearchInline();
  const { data: locations, isLoading: isLocationsLoading } = useLocations();
  const { data: changeReasonsList, isLoading: isChangeReasonsLoading } = useOrganizationListByType(
    ApiOrganizationListType.employee_position_change_reasons
  );

  const changeReasonOptions = useMemo<SelectItem[]>(() => {
    return (changeReasonsList?.items || [])
      .filter((item) => !item.is_archived)
      .map((item) => ({ id: item.id, title: item.title }));
  }, [changeReasonsList]);

  const formHook = useForm({
    mode: "onSubmit",
    resolver: zodResolver(EmployeePositionSchemaObject),
    values: {
      date_effective_from: currentPosition.date_effective_from as Date,
      job_profile_id: currentPosition.job_profile?.id ?? null,
      department_id: currentPosition.department?.id ?? null,
      division_id: currentPosition.division?.id ?? null,
      reporting_to_id: currentPosition.manager?.id ?? null,
      location_id: currentPosition.location?.id ?? null,
      comment: currentPosition?.comment ?? "",
      change_reason_id: (currentPosition.change_reason?.id ?? null) as number,
      ...Object.assign(
        {},
        ...customFields.map((field) => {
          const customField = currentPosition.custom_fields.find(
            (item) => item.field.id === field.id
          );
          return getValueForFieldFromValueObject(field, customField?.value, true);
        })
      ),
    },
    shouldUseNativeValidation: false,
  });

  const {
    control,
    register,
    getValues: getPositionValues,
    trigger,
    formState: { errors, isDirty },
  } = formHook;
  const referenceValues = useEntityReferenceValues(customFields, control);
  const { optionsByEntity: referenceOptionsByEntity } = useEntityReferenceOptions(
    customFields,
    referenceValues
  );

  const employeePositionSave = useEmployeePositionSave(control);
  const fieldConfig = useCallback(
    (type: ApiFieldTypes) => fields.find((field) => field.type === type),
    [fields]
  );
  const fieldOrder = useCallback(
    (type: ApiFieldTypes) => fields.findIndex((field) => field.type === type),
    [fields]
  );
  const fieldTitle = (type: ApiFieldTypes, fallback: string) =>
    fieldConfig(type)?.title?.trim() || fallback;

  const changePreview = (key: string, title: string) => {
    setChangeItems((prevItems) =>
      prevItems.map((item) => (item.id == key ? { ...item, newValue: title } : item))
    );
  };

  const canEditPosition = useMemo(() => {
    return canEditField(employee, EmployeeFieldPermission.manage_employee_position);
  }, [canEditField, employee]);

  const onBack = isPreviewChanges ? () => setIsPreviewChanges(false) : undefined;

  const handleActionButton = async () => {
    if (isPreviewChanges) {
      const payload: ApiEmployeeAssignPositionPayload = {
        date_effective_from: getPositionValues("date_effective_from"),
        ...(fieldConfig(ApiFieldTypes.employee_position_division) && {
          division_id: getPositionValues("division_id") ?? null,
        }),
        ...(fieldConfig(ApiFieldTypes.employee_position_department) && {
          department_id: getPositionValues("department_id") ?? null,
        }),
        ...(fieldConfig(ApiFieldTypes.employee_position_job_profile) && {
          job_profile_id: getPositionValues("job_profile_id") ?? null,
        }),
        ...(fieldConfig(ApiFieldTypes.employee_position_reporting_to) && {
          reporting_to_id: getPositionValues("reporting_to_id") ?? null,
        }),
        ...(fieldConfig(ApiFieldTypes.employee_position_location) && {
          location_id: getPositionValues("location_id") ?? null,
        }),
        ...(fieldConfig(ApiFieldTypes.employee_position_comment) && {
          comment: getPositionValues("comment")?.trim() || null,
        }),
        change_reason_id: getPositionValues("change_reason_id"),
        custom_fields: Object.fromEntries(
          customFields.map((field) => [
            field.slug,
            transformFormDataToValue(field, getPositionValues()),
          ])
        ),
      };

      try {
        await employeePositionSave.mutateAsync({
          employeeId: employee.id,
          positionId: employeePosition?.id,
          payload,
        });

        if (employeePosition) {
          analyticsService.trackEvent(analyticEvents.employeePositions.edited, {
            [analyticProperties.id]: employee.id,
          });
        } else {
          analyticsService.trackEvent(analyticEvents.employeePositions.created, {
            [analyticProperties.id]: employee.id,
          });
        }

        close(id);
      } catch {
        return;
      }
    } else {
      const isValid = await trigger();
      if (!isValid) return;

      const positionValues = getPositionValues();
      setChangeItems((prevItems) =>
        prevItems.map((item) => {
          const field = customFields.find((customField) => customField.slug === item.id);
          if (!field) return item;
          return {
            ...item,
            newValue: getCustomFieldPreviewValue(
              field,
              positionValues,
              field.reference_entity ? (referenceOptionsByEntity[field.reference_entity] ?? []) : []
            ),
          };
        })
      );
      setIsPreviewChanges(true);
    }
  };

  let buttonText: string = t("common:next");

  if (isPreviewChanges) {
    buttonText = isDirty ? t("common:save") : t("common:close");
  }

  const actionsButton = (
    <Stack>
      <Button size={"lg"} onClick={handleActionButton} isLoading={employeePositionSave.isPending}>
        {buttonText}
      </Button>
    </Stack>
  );

  return (
    <Modal
      onBack={onBack}
      title={isPreviewChanges ? t("core:preview_changes") : t("core:position")}
      caption={renderEmployeeName(employee)}
      variant={"side"}
      layout={"stretch"}
      actions={actionsButton}
      withCloser={true}
      {...props}
    >
      <Stack>
        {isPreviewChanges && <ChangesPreview items={changeItems} />}
        {!isPreviewChanges && (
          <>
            <Title
              header={t("core:position_details")}
              paddingTop={false}
              paddingBottom
              size={"xs"}
            />
            <form>
              <Stack>
                {fieldConfig(ApiFieldTypes.effective_from) && (
                  <Stack style={{ order: fieldOrder(ApiFieldTypes.effective_from) }} gap="xs">
                    <Controller
                      render={({ field: { value, onChange, name } }) => (
                        <Datepicker
                          value={value}
                          disabled={!canEditPosition}
                          onChange={(value) => {
                            onChange(value);

                            changePreview(name, value ? renderDate(value) : "-");
                          }}
                          required={true}
                          label={fieldTitle(
                            ApiFieldTypes.effective_from,
                            t("common:date_effective_from")
                          )}
                          error={errors.date_effective_from?.message as string | undefined}
                        />
                      )}
                      control={control}
                      name={"date_effective_from"}
                    />
                    <FieldHint hint={fieldConfig(ApiFieldTypes.effective_from)?.hint} />
                  </Stack>
                )}
                {fieldConfig(ApiFieldTypes.employee_position_change_reason) && (
                  <Stack
                    style={{ order: fieldOrder(ApiFieldTypes.employee_position_change_reason) }}
                    gap="xs"
                  >
                    <Controller
                      render={({ field: { value, onChange, name } }) => (
                        <Select
                          label={fieldTitle(
                            ApiFieldTypes.employee_position_change_reason,
                            t("core:employee_position_change_reason")
                          )}
                          options={changeReasonOptions}
                          isLoading={isChangeReasonsLoading}
                          emptyTitle={t("common:select")}
                          value={value}
                          disabled={!canEditPosition}
                          required={isMandatory(ApiFieldTypes.employee_position_change_reason)}
                          searchable
                          onChange={(value, entity) => {
                            onChange(value);

                            changePreview(name, entity?.title ?? "-");
                          }}
                          error={errors.change_reason_id?.message as string | undefined}
                          onEditClick={() => {
                            openModal(OrganizationListEditModal, {
                              type: ApiOrganizationListType.employee_position_change_reasons,
                            });
                          }}
                        />
                      )}
                      control={control}
                      name={"change_reason_id"}
                    />
                    <FieldHint
                      hint={fieldConfig(ApiFieldTypes.employee_position_change_reason)?.hint}
                    />
                  </Stack>
                )}
                {fieldConfig(ApiFieldTypes.employee_position_job_profile) && (
                  <Stack
                    style={{ order: fieldOrder(ApiFieldTypes.employee_position_job_profile) }}
                    gap="xs"
                  >
                    <Controller
                      render={({ field: { value, onChange, name } }) => (
                        <JobProfileSelect
                          label={fieldTitle(
                            ApiFieldTypes.employee_position_job_profile,
                            t("core:job_profile")
                          )}
                          value={value}
                          disabled={!canEditPosition}
                          isClearable
                          onChange={(value, entity) => {
                            onChange(value);
                            changePreview(name, entity?.title ?? "-");
                          }}
                          required={isMandatory(ApiFieldTypes.employee_position_job_profile)}
                          error={errors.job_profile_id?.message as string | undefined}
                        />
                      )}
                      control={control}
                      name={"job_profile_id"}
                    />
                    <FieldHint
                      hint={fieldConfig(ApiFieldTypes.employee_position_job_profile)?.hint}
                    />
                  </Stack>
                )}

                {fieldConfig(ApiFieldTypes.employee_position_reporting_to) && (
                  <Stack
                    style={{ order: fieldOrder(ApiFieldTypes.employee_position_reporting_to) }}
                    gap="xs"
                  >
                    <Controller
                      render={({ field: { value, onChange, name } }) => (
                        <Select
                          label={fieldTitle(
                            ApiFieldTypes.employee_position_reporting_to,
                            t("core:manager")
                          )}
                          emptyTitle={t("common:select")}
                          isLoading={managerSearch.isLoading}
                          showCaption={true}
                          showAvatar={true}
                          value={value}
                          disabled={!canEditPosition}
                          options={
                            currentPosition?.manager
                              ? [
                                  {
                                    id: currentPosition.manager.id,
                                    title: renderEmployeeName(currentPosition.manager),
                                  },
                                ]
                              : []
                          }
                          searchable
                          isClearable
                          required={isMandatory(ApiFieldTypes.employee_position_reporting_to)}
                          onSearchOptions={(text) => managerSearch.search(text)}
                          searchOptions={managerSearch.options}
                          loadOptionsOnOpen
                          onChange={(value, entity) => {
                            onChange(value);

                            changePreview(name, entity?.title ?? "-");
                          }}
                          error={errors.reporting_to_id?.message as string | undefined}
                        />
                      )}
                      control={control}
                      name={"reporting_to_id"}
                    />
                    <FieldHint
                      hint={fieldConfig(ApiFieldTypes.employee_position_reporting_to)?.hint}
                    />
                  </Stack>
                )}

                {fieldConfig(ApiFieldTypes.employee_position_department) && (
                  <Stack
                    style={{ order: fieldOrder(ApiFieldTypes.employee_position_department) }}
                    gap="xs"
                  >
                    <Controller
                      render={({ field: { value, onChange, name } }) => (
                        <Select
                          label={fieldTitle(
                            ApiFieldTypes.employee_position_department,
                            t("core:department")
                          )}
                          options={departments.data}
                          emptyTitle={t("common:select")}
                          value={value}
                          disabled={!canEditPosition}
                          onChange={(value, entity) => {
                            onChange(value);

                            changePreview(name, entity?.title ?? "-");
                          }}
                          searchable
                          isClearable
                          required={isMandatory(ApiFieldTypes.employee_position_department)}
                          error={errors.department_id?.message as string | undefined}
                        />
                      )}
                      control={control}
                      name={"department_id"}
                    />
                    <FieldHint
                      hint={fieldConfig(ApiFieldTypes.employee_position_department)?.hint}
                    />
                  </Stack>
                )}

                {fieldConfig(ApiFieldTypes.employee_position_division) && (
                  <Stack
                    style={{ order: fieldOrder(ApiFieldTypes.employee_position_division) }}
                    gap="xs"
                  >
                    <Controller
                      render={({ field: { value, onChange, name } }) => (
                        <Select
                          label={fieldTitle(
                            ApiFieldTypes.employee_position_division,
                            t("core:division")
                          )}
                          options={currentPosition?.division ? [currentPosition.division] : []}
                          emptyTitle={t("common:select")}
                          value={value}
                          disabled={!canEditPosition}
                          onChange={(value, entity) => {
                            onChange(value);

                            changePreview(name, entity?.title ?? "-");
                          }}
                          searchable
                          isClearable
                          loadOptionsOnOpen
                          required={isMandatory(ApiFieldTypes.employee_position_division)}
                          onSearchOptions={(text) => divisions.search(text)}
                          searchOptions={divisions.options}
                          error={errors.division_id?.message as string | undefined}
                        />
                      )}
                      control={control}
                      name={"division_id"}
                    />
                    <FieldHint hint={fieldConfig(ApiFieldTypes.employee_position_division)?.hint} />
                  </Stack>
                )}

                {fieldConfig(ApiFieldTypes.employee_position_location) && (
                  <Stack
                    style={{ order: fieldOrder(ApiFieldTypes.employee_position_location) }}
                    gap="xs"
                  >
                    <Controller
                      render={({ field: { value, onChange, name } }) => (
                        <Select
                          label={fieldTitle(
                            ApiFieldTypes.employee_position_location,
                            t("common:location")
                          )}
                          options={locations}
                          emptyTitle={t("common:select")}
                          isLoading={isLocationsLoading}
                          disabled={!canEditPosition}
                          value={value}
                          searchable
                          isClearable
                          required={isMandatory(ApiFieldTypes.employee_position_location)}
                          onChange={(value, entity) => {
                            onChange(value);

                            changePreview(name, entity?.title ?? "-");
                          }}
                          error={errors.location_id?.message as string | undefined}
                        />
                      )}
                      control={control}
                      name={"location_id"}
                    />
                    <FieldHint hint={fieldConfig(ApiFieldTypes.employee_position_location)?.hint} />
                  </Stack>
                )}

                {fieldConfig(ApiFieldTypes.employee_position_comment) && (
                  <Stack
                    style={{ order: fieldOrder(ApiFieldTypes.employee_position_comment) }}
                    gap="xs"
                  >
                    <Textarea
                      onInput={(el) => {
                        changePreview("comment", el.currentTarget?.value ?? "-");
                      }}
                      disabled={!canEditPosition}
                      label={fieldTitle(
                        ApiFieldTypes.employee_position_comment,
                        t("core:add_comment_to_position")
                      )}
                      required={isMandatory(ApiFieldTypes.employee_position_comment)}
                      {...register("comment")}
                    />
                    <FieldHint hint={fieldConfig(ApiFieldTypes.employee_position_comment)?.hint} />
                  </Stack>
                )}
                {customFields.map((field) => (
                  <Stack key={field.id} style={{ order: fields.indexOf(field) }} gap="xs">
                    {getFieldByType({
                      field,
                      formHook,
                      canView: true,
                      canEdit: canEditPosition,
                      label: field.title,
                      options: field.options.map((option) => ({
                        id: Number(option.id),
                        title: option.title,
                      })),
                    })}
                  </Stack>
                ))}
              </Stack>
            </form>
          </>
        )}
      </Stack>
    </Modal>
  );
}
